import {LABEL_ARCHIVE_TYPE, type LabelArchiveType} from '../constants/values'

/**
 * Converts the `archived` URL parameter to a LabelArchiveType enum value.
 *
 * This helper only translates public URL state; it does not enforce repository or viewer capability.
 * The labels endpoint owns that enforcement: it ignores archive filtering when archiving is disabled and
 * defaults a missing filter to UNARCHIVED when archiving is enabled. UI callers must separately use the
 * issues layout payload to decide whether to expose archiving controls.
 *
 * @param archivedParam - The `archived` query parameter value ('true', 'false', or null)
 * @returns ARCHIVED when param is 'true', UNARCHIVED when 'false' or null (defaults to active labels)
 *
 * @example
 * parseArchivedParam('true')   // => 'ARCHIVED'
 * parseArchivedParam('false')  // => 'UNARCHIVED'
 * parseArchivedParam(null)     // => 'UNARCHIVED' (default to active)
 */
export function parseArchivedParam(archivedParam: string | null): LabelArchiveType {
  const {ARCHIVED, UNARCHIVED} = LABEL_ARCHIVE_TYPE
  return archivedParam === 'true' ? ARCHIVED : UNARCHIVED
}

/**
 * Converts a LabelArchiveType enum value to the `archived` URL parameter string.
 *
 * @param archiveType - The archive type enum value
 * @returns 'true' for ARCHIVED, 'false' for UNARCHIVED
 *
 * @example
 * serializeArchivedParam('ARCHIVED')    // => 'true'
 * serializeArchivedParam('UNARCHIVED')  // => 'false'
 */
export function serializeArchivedParam(archiveType: LabelArchiveType): string {
  const {ARCHIVED} = LABEL_ARCHIVE_TYPE
  return archiveType === ARCHIVED ? 'true' : 'false'
}

/**
 * Checks if the `archived` URL parameter indicates archived labels.
 *
 * This helper only interprets URL state. Callers must use the issues layout payload separately when deciding
 * whether archiving UI should be rendered; the labels endpoint enforces repository and viewer capability.
 *
 * @param archivedParam - The `archived` query parameter value ('true', 'false', or null)
 * @returns true when param is 'true', false when 'false' or null (defaults to active labels)
 *
 * @example
 * isArchivedParam('true')   // => true
 * isArchivedParam('false')  // => false
 * isArchivedParam(null)     // => false (default to active)
 */
export function isArchivedParam(archivedParam: string | null): boolean {
  return parseArchivedParam(archivedParam) === LABEL_ARCHIVE_TYPE.ARCHIVED
}
